<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Hero Section -->
    <section class="relative h-[480px] overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-fuchsia-600">
      <div class="absolute inset-0">
        <img
          src="https://images.ctfassets.net/zhgoh1a85x2k/2hQSbJSBSKFYQRz3x7uGgM/27ccd0bb45117ec485b69baf6edfb7e7/category_mobile_top_up_asset.svg"
          alt=""
          class="w-full h-full object-contain object-center opacity-20"
        >
      </div>
      <div class="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>

      <div class="absolute inset-0 flex items-center">
        <div class="w-full px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 pt-[80px] md:pt-[120px]">
          <div class="flex flex-col md:flex-row items-start md:items-center gap-8 max-w-6xl mx-auto">
            <div class="text-white md:w-1/2">
              <h1 class="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4">
                Recharge<br>
                <span class="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-amber-400">
                  mobile
                </span>
              </h1>
              <p class="text-lg lg:text-xl text-white/80">
                Restez connecté, quelle que soit la distance
              </p>
            </div>

            <!-- Selected Country Display -->
            <div v-if="selectedCountry" class="w-full md:w-[320px] md:ml-auto">
              <div class="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <div class="flex items-center gap-4 mb-4">
                  <img
                    v-if="selectedCountry.localLogoUrl"
                    :src="selectedCountry.localLogoUrl"
                    :alt="selectedCountry.countryName"
                    class="w-12 h-12 object-contain"
                  >
                  <span v-else class="text-4xl">{{ getCountryFlag() }}</span>
                  <div class="text-white">
                    <p class="font-bold text-lg">{{ getCountryName() }}</p>
                    <p class="text-sm text-white/70">{{ getCountryCurrency() }}</p>
                  </div>
                </div>
                <p class="text-white/80 text-sm">
                  Les opérateurs disponibles pour ce pays s'affichent ci-dessous
                </p>
              </div>
            </div>

            <!-- No Country Selected State -->
            <div v-else class="w-full md:w-[320px] md:ml-auto">
              <div class="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <p class="text-white font-semibold mb-2">Sélectionnez un pays</p>
                <p class="text-white/70 text-sm">
                  Utilisez le sélecteur de pays dans le header pour commencer
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="flex-1 py-12 bg-white">
      <div class="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">

        <!-- No Country Selected State -->
        <div v-if="!selectedCountry" class="text-center py-20">
          <div class="text-7xl mb-6">🌍</div>
          <h3 class="text-2xl font-bold text-gray-900 mb-3">
            Sélectionnez un pays
          </h3>
          <p class="text-gray-600 text-lg">
            Utilisez le sélecteur de pays dans le header pour voir les opérateurs disponibles.
          </p>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="text-center py-20">
          <div class="inline-block w-12 h-12 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="mt-6 text-gray-600 text-lg">Chargement des opérateurs...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl text-center max-w-lg mx-auto mt-8">
          {{ error }}
        </div>

        <!-- Operators Section -->
        <template v-else>
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
            Opérateurs disponibles
          </h2>

          <!-- Operation Type Tabs -->
          <div class="flex gap-3 overflow-x-auto pb-6 scrollbar-hide mb-6">
            <button
              v-for="type in operationTypes"
              :key="type.key"
              @click="selectOperationType(type)"
              class="flex items-center gap-2 px-5 py-3 rounded-2xl whitespace-nowrap transition-all font-semibold text-sm"
              :class="operationType === type.key
                ? 'bg-gray-900 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            >
              <span v-html="type.icon"></span>
              <span>{{ type.label }}</span>
            </button>
          </div>

          <!-- Operators Grid -->
          <div v-if="operators.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <div
              v-for="operator in operators"
              :key="operator.operatorId"
              @click="selectOperator(operator)"
              class="operator-card group"
            >
              <div class="operator-card-inner">
                <div class="operator-image-wrapper">
                  <img
                    v-if="operator.logoUrls && operator.logoUrls.length > 0"
                    :alt="operator.name || operator.operatorName"
                    loading="lazy"
                    class="operator-image"
                    :src="operator.logoUrls[1] || operator.logoUrls[0]"
                  >
                  <div v-else class="operator-fallback">
                    <span class="text-4xl">{{ getOperatorEmoji(operator.name || operator.operatorName) }}</span>
                  </div>
                </div>
                <div class="operator-info">
                  <p class="operator-name">{{ operator.name || operator.operatorName }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State (when country is selected but no operators) -->
          <div v-else class="text-center py-20">
            <div class="text-7xl mb-6">📭</div>
            <h3 class="text-2xl font-bold text-gray-900 mb-3">
              Aucun opérateur disponible
            </h3>
            <p class="text-gray-600 text-lg mb-6">
              Aucun opérateur {{ getOperationTypeLabel() }} disponible pour ce pays.
            </p>
            <p class="text-gray-500">
              Essayez un autre type de recharge ou sélectionnez un autre pays.
            </p>
          </div>
        </template>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores } = proxy;

const loading = ref(true);
const error = ref(null);
const operators = ref([]);

// Selected country from global store
const selectedCountry = computed(() => $stores.global.state.selectedCountry);

// Operation type
const operationType = ref('airtime');
const operationTypes = [
  {
    key: 'airtime',
    label: 'Crédit',
    icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>'
  },
  {
    key: 'data',
    label: 'Internet',
    icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/></svg>'
  },
  {
    key: 'bundle',
    label: 'Forfaits',
    icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>'
  }
];

// Watch for country changes and refetch operators
watch(() => $stores.global.state.selectedCountry, () => {
  if ($stores.global.state.selectedCountry) {
    fetchOperators();
  }
});

onMounted(async () => {
  if ($stores.global.state.selectedCountry) {
    await fetchOperators();
  } else {
    loading.value = false;
  }
});

const fetchOperators = async () => {
  const country = $stores.global.state.selectedCountry;
  if (!country) {
    operators.value = [];
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off('products.fetch_operators_success', successHandler);
        bus.off('products.fetch_operators_error', errorHandler);
        operators.value = data.data?.operatorList || data.data || data || [];
        loading.value = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off('products.fetch_operators_success', successHandler);
        bus.off('products.fetch_operators_error', errorHandler);
        if (err?.response?.status === 404 || err?.status === 404) {
          operators.value = [];
        } else {
          error.value = 'Erreur lors du chargement des opérateurs';
        }
        loading.value = false;
        reject(err);
      };

      bus.on('products.fetch_operators_success', successHandler);
      bus.on('products.fetch_operators_error', errorHandler);

      bus.emit('products.fetch_operators', {
        countryCode: country.countryCode,
        type: operationType.value
      });
    });
  } catch (e) {
    error.value = 'Erreur lors du chargement des opérateurs';
    loading.value = false;
  }
};

const selectOperationType = (type) => {
  operationType.value = type.key;
  fetchOperators();
};

const getCountryFlag = () => {
  if (!selectedCountry.value) return '🌍';
  const countryCode = selectedCountry.value.countryCode;
  const flags = {
    'BJ': '🇧🇯', 'TG': '🇹🇬', 'CI': '🇨🇮', 'SN': '🇸🇳', 'ML': '🇲🇱',
    'FR': '🇫🇷', 'BE': '🇧🇪', 'CH': '🇨🇭', 'CM': '🇨🇲', 'CD': '🇨🇩',
    'NE': '🇳🇪', 'BF': '🇧🇫', 'GN': '🇬🇳'
  };
  return flags[countryCode] || '🌍';
};

const getCountryName = () => {
  return selectedCountry.value?.countryName || 'Sélectionner un pays';
};

const getCountryCurrency = () => {
  return selectedCountry.value ? `${selectedCountry.value.currency}` : '';
};

const getCountryCurrencyCode = () => {
  return selectedCountry.value?.currency || 'FCFA';
};

const getPhoneCode = () => {
  if (!selectedCountry.value) return '+XXX';
  return selectedCountry.value.phonePrefix || `+${selectedCountry.value.callingCode}` || '+XXX';
};

const selectOperator = async (operator) => {
  const country = $stores.global.state.selectedCountry;

  // Redirect to product detail page with simplified URL format: operatorId-countryCode
  router.push({
    path: `/mobile-top-up/${operator.operatorId}-${country?.countryCode || 'BJ'}`
  });
};

const getOperatorEmoji = (name) => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('mtn')) return '🟡';
  if (lower.includes('moov')) return '🔵';
  if (lower.includes('orange')) return '🟠';
  if (lower.includes('airtel')) return '🔴';
  return '📱';
};

const getOperationTypeLabel = () => {
  if (operationType.value === 'airtime') return 'de crédit';
  if (operationType.value === 'data') return 'de data';
  if (operationType.value === 'bundle') return 'de forfaits';
  return '';
};

const goToCheckout = (data, operator) => {
  const { amount, email, phone } = data;
  const country = $stores.global.state.selectedCountry;

  router.push({
    path: '/checkout/quick',
    query: {
      operatorId: operator.operatorId,
      operatorName: operator.name || operator.operatorName,
      amount: amount,
      email: email,
      phone: phone,
      orderType: operationType.value,
      operationName: operationType.value,
      countryCode: country?.countryCode || 'BJ'
    }
  });
};
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Operator Card */
.operator-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.operator-card-inner {
  background: white;
  border-radius: 20px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.operator-card:hover .operator-card-inner {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: rgba(1, 243, 122, 0.3);
  transform: translateY(-4px);
}

.operator-image-wrapper {
  width: 100%;
  aspect-ratio: 188 / 148;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.operator-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
  transition: transform 0.3s ease;
}

.operator-card:hover .operator-image {
  transform: scale(1.05);
}

.operator-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(1, 243, 122, 0.1) 0%, rgba(0, 217, 255, 0.05) 100%);
}

.operator-info {
  text-align: center;
}

.operator-name {
  font-family: 'Work Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
